/**
 * 图形拖拽绘制：直线、矩形、圆、三角、多边形、星形、心形
 * 坐标与落点归属（页 / 工作区）与 useTextDraw 一致。
 */

import { useState, useRef, useCallback, useEffect, type RefObject } from 'react';
import { screenToWorld } from '@/components/canvas/document/coordinates';
import type { TextInsertVia } from '@/components/canvas/workspace/model/elementInsertTarget';
import type { ShapeInsertKind } from '@/components/canvas/elements/shape/toolbar/shapeInsertKinds';
import {
  LINE_DRAW_DEFAULT,
  SHAPE_DRAW_DEFAULTS,
} from '@/components/canvas/elements/shape/toolbar/shapeInsertKinds';
import {
  boxFromDrag,
  lineFromDrag,
  previewFromDrag,
  rectBoxFromDrag,
  squareBoxFromDrag,
  type ShapeDrawPreview,
} from './shapeDrawGeometry';

interface UseShapeDrawOptions {
  kind: ShapeInsertKind | null;
  pageRef: RefObject<HTMLDivElement | null>;
  stageHostRef?: RefObject<HTMLDivElement | null>;
  panX?: number;
  panY?: number;
  zoom: number;
  isSpacePressed: boolean;
  addShapeElementAt: (payload: {
    kind: ShapeInsertKind;
    x: number;
    y: number;
    width: number;
    height: number;
    endX?: number;
    endY?: number;
    via?: TextInsertVia;
    pageId?: string;
  }) => string | null;
  onDrawComplete?: () => void;
}

export function useShapeDraw({
  kind,
  pageRef,
  stageHostRef,
  panX = 0,
  panY = 0,
  zoom,
  isSpacePressed,
  addShapeElementAt,
  onDrawComplete,
}: UseShapeDrawOptions) {
  const [preview, setPreview] = useState<ShapeDrawPreview | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const startRef = useRef<{ x: number; y: number } | null>(null);
  const shiftRef = useRef(false);
  const insertTargetRef = useRef<{ via: TextInsertVia; pageId?: string }>({
    via: 'workspace',
  });
  const kindRef = useRef(kind);
  kindRef.current = kind;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panXRef = useRef(panX);
  panXRef.current = panX;
  const panYRef = useRef(panY);
  panYRef.current = panY;
  const addShapeRef = useRef(addShapeElementAt);
  addShapeRef.current = addShapeElementAt;
  const onCompleteRef = useRef(onDrawComplete);
  onCompleteRef.current = onDrawComplete;

  const toDrawCoords = useCallback((clientX: number, clientY: number) => {
    const pageEl = pageRef.current;
    if (pageEl) {
      const rect = pageEl.getBoundingClientRect();
      return {
        x: (clientX - rect.left) / zoomRef.current,
        y: (clientY - rect.top) / zoomRef.current,
      };
    }
    const host = stageHostRef?.current;
    if (host) {
      const rect = host.getBoundingClientRect();
      return screenToWorld(
        clientX,
        clientY,
        rect,
        panXRef.current,
        panYRef.current,
        zoomRef.current,
      );
    }
    return { x: 0, y: 0 };
  }, [pageRef, stageHostRef]);

  useEffect(() => {
    if (!kind) {
      startRef.current = null;
      setPreview(null);
      setIsDrawing(false);
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftRef.current = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftRef.current = false;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!startRef.current || !kindRef.current) return;
      const pos = toDrawCoords(e.clientX, e.clientY);
      const { x: sx, y: sy } = startRef.current;
      shiftRef.current = e.shiftKey;
      setPreview(
        previewFromDrag(kindRef.current, sx, sy, pos.x, pos.y, shiftRef.current),
      );
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!startRef.current || !kindRef.current) return;
      const activeKind = kindRef.current;
      const pos = toDrawCoords(e.clientX, e.clientY);
      const { x: sx, y: sy } = startRef.current;
      startRef.current = null;
      setPreview(null);
      setIsDrawing(false);
      shiftRef.current = e.shiftKey;

      const insertOpts = insertTargetRef.current;

      if (activeKind === 'line') {
        const line = lineFromDrag(
          sx,
          sy,
          pos.x,
          pos.y,
          shiftRef.current,
          LINE_DRAW_DEFAULT.width,
        );
        addShapeRef.current({
          kind: 'line',
          x: line.x,
          y: line.y,
          width: line.width,
          height: line.height,
          endX: line.endX,
          endY: line.endY,
          ...insertOpts,
        });
      } else {
        const defaults = SHAPE_DRAW_DEFAULTS[activeKind];
        const rect =
          activeKind === 'circle'
            ? squareBoxFromDrag(sx, sy, pos.x, pos.y, defaults.width)
            : activeKind === 'rect'
              ? rectBoxFromDrag(sx, sy, pos.x, pos.y, defaults.width)
              : boxFromDrag(sx, sy, pos.x, pos.y, defaults);
        addShapeRef.current({
          kind: activeKind,
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          ...insertOpts,
        });
      }

      onCompleteRef.current?.();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [kind, toDrawCoords]);

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!kind) return false;
      if (e.button !== 0) return false;
      if (isSpacePressed) return false;

      const target = e.target as HTMLElement;
      if (target.closest('[data-element-hit]') || target.closest('[data-handle]')) {
        return false;
      }

      e.preventDefault();
      if (pageRef.current) {
        insertTargetRef.current = {
          via: 'page',
          pageId: pageRef.current.getAttribute('data-page') ?? undefined,
        };
      } else {
        insertTargetRef.current = { via: 'workspace' };
      }
      startRef.current = toDrawCoords(e.clientX, e.clientY);
      shiftRef.current = e.shiftKey;
      setIsDrawing(true);
      setPreview(null);
      return true;
    },
    [kind, isSpacePressed, toDrawCoords, pageRef],
  );

  return {
    preview,
    handleCanvasMouseDown,
    isDrawing,
  };
}

export type { ShapeDrawPreview };
